import { useEffect } from 'react';
import nookies from 'nookies';
import CategoryList from 'components/CategoryList';
import Loader from 'components/LandingPage/Loader.jsx';
import PageMeta from 'components/PageMeta';
import { DEFAULT_LOCATION } from 'config/serverConfig';
import getContentData from 'data/contentstack/get-content-data';
import getPlusMenu from 'data/dutchie/menu';
import universalHydration from 'data/universal-hydration';
import useGetProducts from 'hooks/useGetProducts';
import sspMiddleware from 'middleware/gssp';
import setCaching from 'utils/cache-control';
import getCanonicalUrl from 'utils/get-canonical-url';
import getCorrelationId from 'utils/get-correlation-id';
import { sendPageView, setMetricsData } from 'utils/metrics';

const OPTIONS_TYPE = 'Categories';
const DUTCHIE_DEFAULT_SORT = 'most_popular';

export default function CategoryListPageAll({
  backInStockText,
  badgeImages = [],
  canonical,
  category,
  categoryMeta,
  closeoutText,
  customSectionContent,
  defaultCategoryImages = [],
  facets: propFacets,
  featuredProductText,
  filtersImage,
  groupedDiscountsContent,
  packagedDateTooltipInfo,
  heading,
  location,
  menu,
  newProductText,
  noItemsContent,
  path,
  placeholder,
  packagedDateConfiguration,
  priceDropText,
  products: propProducts = [],
  promotionTiles,
  seo,
  sortOptions,
  terpeneTooltipInfo,
  total: propTotal,
}) {
  // useGetProducts handles all products update requests for all shopping pages
  const { facets, products, total, storeSelection } = useGetProducts('all', {
    propFacets,
    propProducts,
    propTotal,
    ssrStore: location,
    ssrMenu: menu,
  });

  const {
    meta_title: title,
    meta_description: description,
    keywords,
  } = seo || {};

  const { metaDescription, metaKeywords, metaTitle } = categoryMeta || {};

  const { component_list: components = [] } = customSectionContent || {};

  useEffect(() => {
    setMetricsData({
      location,
      menu,
      pageType: 'category_page',
      title: metaTitle || title,
    });
    sendPageView(title);
  }, [metaTitle, title, location, menu]);

  return (
    <>
      <PageMeta
        canonical={canonical}
        description={metaDescription || description}
        keywords={metaKeywords || keywords}
        title={metaTitle || title}
      />
      <CategoryList
        backInStockText={backInStockText}
        badgeImages={badgeImages}
        category={category}
        closeoutText={closeoutText}
        defaultCategoryImages={defaultCategoryImages}
        facets={facets}
        featuredProductText={featuredProductText}
        filtersImage={filtersImage}
        groupedDiscountsContent={groupedDiscountsContent}
        packagedDateTooltipInfo={packagedDateTooltipInfo}
        heading={heading}
        location={storeSelection}
        newProductText={newProductText}
        noItemsContent={noItemsContent}
        path={path}
        placeholder={placeholder}
        packagedDateConfiguration={packagedDateConfiguration}
        priceDropText={priceDropText}
        products={products}
        promotionTiles={promotionTiles}
        shoppingOptionsType={OPTIONS_TYPE}
        sorts={sortOptions}
        terpeneTooltipInfo={terpeneTooltipInfo}
        total={total}
      />
      {components.map((component) => {
        const Component = Loader(component._content_type_uid);

        if (!Component) return null;

        return <Component key={component.uid} {...component} />;
      })}
    </>
  );
}

export const getServerSideProps = sspMiddleware((store) => async (ctx) => {
  setCaching('short', ctx.res);
  const { log } = ctx.req;
  const correlationId = getCorrelationId({ request: ctx.req });

  const slug = 'all_products';

  const { filters = '', sort = '', search = '' } = ctx.query;

  const { store: location = DEFAULT_LOCATION, menu = 'recreational' } =
    nookies.get(ctx);

  const appliedSort = sort ?? DUTCHIE_DEFAULT_SORT;

  const path = '/shop/all';

  const [
    universal,
    productsData,
    shopPageContent,
    shopPageCustomSection,
    groupedDiscounts,
  ] = await Promise.allSettled([
    universalHydration({ ctx, store, correlationId, log }),
    getPlusMenu({
      correlationId,
      filters,
      isShopAll: true,
      log,
      menu,
      sort: appliedSort,
      storeName: location,
      search,
    }),
    getContentData({
      id: 'shop_product_page',
      correlationId,
      log,
      params: {
        // eslint-disable-next-line camelcase
        include_branch: true,
        include: [
          'promotion_tiles',
          'product_labels',
          'newsletter',
          'no_items_found',
          'shop_content',
        ],
      },
    }),
    getContentData({
      id: 'shop_page_custom_sections',
      correlationId,
      log,
      params: {
        // eslint-disable-next-line camelcase
        include_branch: true,
        include: [
          'component_list',
          'component_list.items',
          'component_list.email_capture',
        ],
        query: `{"url": "${path}"}`,
      },
    }),
    getContentData({
      id: 'grouped_product_discounts',
      correlationId,
      log,
      params: {
        // eslint-disable-next-line camelcase
        group_id: 'tiered-flower',
      },
    }),
  ]);

  const props = {
    backInStockText: 'BACK IN STOCK',
    canonical: getCanonicalUrl(path),
    category: slug,
    categoryMeta: {},
    closeoutText: 'LAST CALL',
    facets: [],
    heading: '',
    location,
    menu,
    path,
    products: [],
    promotionTiles: [],
    slug,
    total: 0,
  };

  if (universal.status === 'fulfilled') {
    props.footer = universal.value.footer;
    props.header = universal.value.header;
  }

  if (productsData.status === 'fulfilled') {
    const {
      category: categoryMeta = {},
      facets,
      products,
      total,
    } = productsData.value;
    props.categoryMeta = categoryMeta;
    props.facets = facets;
    props.products = products;
    props.total = total;
  }

  if (shopPageContent.status === 'fulfilled') {
    const shopPageContentData = shopPageContent.value?.data?.[0] ?? null;

    const productLabels = shopPageContentData?.product_labels?.[0] ?? null;
    if (productLabels) {
      const {
        back_in_stock_text: backInStockText,
        badge_images: badgeImages = [],
        closeout_text: closeoutText,
        default_category_images: defaultCategoryImages,
        featured_product_text: featuredProductText,
        packaged_date_tooltip_info: packagedDateTooltipInfo,
        new_product_text: newProductText,
        packaged_date_configuration: packagedDateConfiguration,
        price_drop_text: priceDropText,
        terpene_tooltip_info: terpeneTooltipInfo,
      } = productLabels;
      props.backInStockText = backInStockText;
      props.badgeImages = badgeImages;
      props.closeoutText = closeoutText;
      props.defaultCategoryImages = defaultCategoryImages;
      props.featuredProductText = featuredProductText;
      props.packagedDateTooltipInfo = packagedDateTooltipInfo;
      props.newProductText = newProductText;
      props.priceDropText = priceDropText;
      props.packagedDateConfiguration = packagedDateConfiguration;
      props.terpeneTooltipInfo = terpeneTooltipInfo;
    }

    const shopContent = shopPageContentData?.shop_content?.[0] ?? null;
    if (shopContent) {
      const {
        category_texts: categoryTexts = {},
        filters_image: filtersImage = {},
        icons = {},
        seo = {},
        sort = {},
      } = shopContent ?? {};
      props.filtersImage = filtersImage.url;
      props.heading = categoryTexts[slug];
      props.sortOptions = sort;
      props.seo = seo;
      props.placeholder = icons.spinner_v2?.url;
    }

    const promotionTiles =
      shopPageContentData?.promotion_tiles?.[0].promotion_tiles ?? [];
    if (promotionTiles) {
      props.promotionTiles = promotionTiles;
    }

    const noItemsContent = shopPageContentData?.no_items_found?.[0] ?? null;
    if (noItemsContent) {
      props.noItemsContent = noItemsContent;
    }
  }

  if (shopPageCustomSection.status === 'fulfilled') {
    props.customSectionContent = shopPageCustomSection?.value?.data?.[0] ?? {};
  }

  if (groupedDiscounts.status === 'fulfilled') {
    props.groupedDiscountsContent =
      groupedDiscounts?.value?.data?.[0]?.discounts ?? [];
  }

  props.metrics = {
    title: props.categoryMeta.metaTitle || props.seo?.meta_title,
    pageType: 'category_page',
    location,
    menu,
  };

  return {
    props,
  };
});
